import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleModal } from 'apps/auth/actions/appAction';
import { changeFormFieldValue } from 'apps/auth/actions/formAction';
import { Badge } from 'apps/auth/components/common/Badge/Badge';
import { Button } from 'apps/auth/components/common/Button/Button';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { PanelContent, PanelFrame, PanelRightBlock } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import AccountPaymentMethod from 'apps/auth/components/paysendPlus/subscription/AccountPaymentMethod';
import AddNewCardModal from 'apps/auth/components/paysendPlus/subscription/AddNewCardModal';
import CardPaymentMethod from 'apps/auth/components/paysendPlus/subscription/CardPaymentMethod';
import FreePaymentSource from 'apps/auth/components/paysendPlus/subscription/FreePaymentSource';
import PaysendPlusPrecheckPopup from 'apps/auth/components/paysendPlus/subscription/PaysendPlusPrecheckPopup';
import { CARD_TO_SUBSCRIPTION, GA_POCKET_TO_SUBSCRIPTION, PAYMENT_METHOD_SCREEN, PLAN_ID_LIST_SCREEN } from 'apps/auth/components/paysendPlus/utils';
import TextPopup from 'apps/auth/components/sharedComponents/popups/TextPopup';
import BasePage from 'apps/auth/components/utils/BasePage';
import { PLUS_SELECT_PAYMENT_CLICKED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_METHOD_AMPLITUDE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { ADD_NEW_CARD, USE_LINKED_CARD } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { PLUS_SUBSCRIBE_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_PLUS_MAIN_PAGE, AUTH_PLUS_SUBSCRIBE_ACTION, GLOBAL_ACCOUNT, URL_IMAGES_BANKS } from 'apps/auth/constants/UrlConstants';
import { toLowerCase } from 'apps/auth/helpers/utils';
import { useExtra, useForm, usePageMessages } from 'apps/auth/hooks/selectors';

const PaysendPlusSubscribePage = (props) => {
    const [screen, setScreen] = useState(PLAN_ID_LIST_SCREEN);
    const [disabled, setDisabled] = useState(true);
    const popupRef = useRef(null);
    const submitRef = useRef(null);
    const { linkedCards, accounts, subscriptionTariffs, subscriptionTariffsUI, maxCardsLimit, isPaysendEmployee, otherSourceShow, otherSource } = useExtra();
    const {
        paymentMethodsTitle,
        paysendAccountsTitle,
        linkedCardsTitle,
        addCard,
        defaultBankName,
        planSelectTitle,
        continueButton,
        gotIt,
        dialogReachCardsLimit,
        otherSourceTitle,
    } = usePageMessages();
    const { fields } = useForm(PLUS_SUBSCRIBE_FORM_NAME);

    const selectPlan = (e, tariff) => {
        setDisabled(false);
        props.changeFormFieldValue(PLUS_SUBSCRIBE_FORM_NAME, tariff?.id, 'planId');
    };

    const getSelectedTariff = () => {
        return subscriptionTariffs.find((t) => t.id === fields?.planId?.value);
    };

    const onAddNewClick = () => {
        amplitude.getInstance().logEvent(PLUS_SELECT_PAYMENT_CLICKED, {
            [PAYMENT_METHOD_AMPLITUDE]: ADD_NEW_CARD,
        });
        if (maxCardsLimit) {
            popupRef.current?.open(
                <TextPopup
                    text={dialogReachCardsLimit}
                    popupRef={popupRef}
                    buttonText={gotIt}
                    onClick={() => {
                        popupRef.current?.close();
                    }}
                />,
            );
        } else {
            props.toggleModal(
                true,
                <AddNewCardModal postAction={'precheque'} tariff={getSelectedTariff()} popupRef={popupRef} closeModal={() => props.toggleModal(false)} />,
            );
        }
    };

    const openPrecheck = (card = null, paymentMethod) => {
        amplitude.getInstance().logEvent(PLUS_SELECT_PAYMENT_CLICKED, {
            [PAYMENT_METHOD_AMPLITUDE]: card ? USE_LINKED_CARD : GLOBAL_ACCOUNT,
        });
        props.changeFormFieldValue(PLUS_SUBSCRIBE_FORM_NAME, paymentMethod, 'pMethodId');
        props.changeFormFieldValue(PLUS_SUBSCRIBE_FORM_NAME, card?.encryptedInfo, 'pMethodEncodedInfo').then(() => {
            popupRef.current?.open(
                <PaysendPlusPrecheckPopup
                    popupRef={popupRef}
                    actionType={'subscription'}
                    action={AUTH_PLUS_SUBSCRIBE_ACTION}
                    tariff={getSelectedTariff()}
                    submitRef={submitRef}
                    selectedCard={card}
                />,
            );
        });
    };

    return (
        <>
            <CommonPage backUrl={AUTH_PLUS_MAIN_PAGE} leftCloseIcon>
                {screen === PLAN_ID_LIST_SCREEN && (
                    <>
                        <HeaderPanel title={planSelectTitle} />
                        {subscriptionTariffsUI.map((tariff) => (
                            <Row indent="adaptive-24-32" key={tariff.id} relative>
                                <Panel
                                    isBordered
                                    hasBorderRadius
                                    isSelectedBordered={tariff.id === fields?.planId?.value}
                                    isHoverable
                                    badgeCaption={tariff.promoLabel}
                                    onClick={(e) => selectPlan(e, tariff)}
                                >
                                    <PanelFrame>
                                        <PanelContent>
                                            <Text variant="div" indent="4px">
                                                {tariff.title}
                                            </Text>
                                            <Text variant="div" size="secondary" color="secondary">
                                                {tariff.description}
                                            </Text>
                                        </PanelContent>
                                        <PanelRightBlock>
                                            <Badge
                                                variant={tariff.id === fields?.planId?.value ? 'primary' : 'secondary'}
                                                size="price"
                                                caption={tariff.amount}
                                                currencyValue={toLowerCase(tariff.currencyCode)}
                                            />
                                        </PanelRightBlock>
                                    </PanelFrame>
                                </Panel>
                            </Row>
                        ))}
                        <FixedBottomPanel isRightAligned>
                            <Button
                                type="button"
                                raised={true}
                                caption={continueButton}
                                onClick={() => {
                                    setScreen(PAYMENT_METHOD_SCREEN);
                                }}
                                ref={submitRef}
                                disabled={disabled}
                            />
                        </FixedBottomPanel>
                    </>
                )}
                {screen === PAYMENT_METHOD_SCREEN && !isPaysendEmployee && (
                    <>
                        <HeaderPanel title={paymentMethodsTitle} />
                        {accounts?.length > 0 && (
                            <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                                {paysendAccountsTitle}
                            </Text>
                        )}
                        {accounts?.length > 0 &&
                            accounts.map((account) => {
                                return (
                                    <AccountPaymentMethod
                                        account={account}
                                        key={account.id}
                                        paymentMethod={GA_POCKET_TO_SUBSCRIPTION}
                                        formName={PLUS_SUBSCRIBE_FORM_NAME}
                                        tariff={getSelectedTariff()}
                                        popupRef={popupRef}
                                        openClick={openPrecheck}
                                    />
                                );
                            })}
                        <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                            {linkedCardsTitle}
                        </Text>
                        <CardPaymentMethod name={addCard} icon="card-plus" onAddNewClick={onAddNewClick} />
                        {Object.values(linkedCards)?.length > 0 &&
                            Object.values(linkedCards).map((card) => {
                                const name = `${card.bankName || defaultBankName} ·· ${card.description}`;
                                return (
                                    <CardPaymentMethod
                                        card={card}
                                        name={name}
                                        key={card.id}
                                        id={card.id}
                                        tariff={getSelectedTariff()}
                                        paymentMethod={CARD_TO_SUBSCRIPTION}
                                        icon={!card.bankId ? 'card' : null}
                                        src={card.bankId ? `${URL_IMAGES_BANKS}/${card.bankId}.png` : null}
                                        diskVariant="tertiary"
                                        popupRef={popupRef}
                                        openClick={openPrecheck}
                                    />
                                );
                            })}
                    </>
                )}
                {screen === PAYMENT_METHOD_SCREEN && isPaysendEmployee && otherSourceShow && (
                    <>
                        <HeaderPanel title={paymentMethodsTitle} />
                        <Text variant="div" size="subheader" color="secondary" indent="adaptive-16-24">
                            {otherSourceTitle}
                        </Text>
                        <FreePaymentSource name={otherSource.name} icon="paysend-logo-globe-circle" popupRef={popupRef} openClick={openPrecheck} />
                    </>
                )}
            </CommonPage>
            <PopupNew ref={popupRef} />
        </>
    );
};

export default connect(null, { toggleModal, changeFormFieldValue })(BasePage(PaysendPlusSubscribePage));
